import { useCallback, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { JsonEditor } from '@/components/JsonEditor';
import { logger } from '@/logger';
import type { StateSnapshot } from '@/types.ts';

import type { Action } from '../../../../../bridge/actions';
import type { UserState } from '../../../../../bridge/types/state.ts';

import { SnapshotManager } from './SnapshotManager';
import { TestingUtils } from './TestingUtils';

export const DemoStateViewer = () => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const [selectedActionType, setSelectedActionType] = useState('');
	const [resetLoading, setResetLoading] = useState(false);
	const [resetError, setResetError] = useState<string | null>(null);

	const actionEditorData = useMemo(
		() =>
			selectedActionType.trim() ? { type: selectedActionType.trim() } : {},
		[selectedActionType],
	);

	const handleLoadSnapshot = (snapshot: StateSnapshot) => {
		bridgeApi.sendAction({
			type: 'do_load_snapshot',
			user_state: snapshot.user_state,
		});
	};

	const handleSaveState = useCallback(
		(data: unknown) => {
			bridgeApi.sendAction({
				type: 'do_load_snapshot',
				user_state: data as UserState,
			});
		},
		[bridgeApi],
	);

	const handleSendAction = useCallback(
		(data: unknown) => {
			bridgeApi.sendAction(data as Action);
		},
		[bridgeApi],
	);

	const handleResetState = useCallback(async () => {
		if (
			!confirm(
				'Are you sure you want to reset state? This will permanently delete all Redis, CosmosDB, and Elasticsearch data for this user.',
			)
		) {
			return;
		}
		setResetLoading(true);
		setResetError(null);
		try {
			// Reset Redis state via bridge action (post-effect also calls the API,
			// but we call directly too as a belt-and-suspenders fallback).
			bridgeApi.sendAction({ type: 'do_reset_user_state' });
			if (userId) {
				await bridgewayApi.delete(`/api/user/${userId}/bulk`);
			}
		} catch (err) {
			logger.error('Reset state failed', err);
			setResetError(err instanceof Error ? err.message : 'Reset failed');
		} finally {
			setResetLoading(false);
		}
	}, [bridgeApi, bridgewayApi, userId]);

	const chatData = bridgeApi.user_state?.current_chat_id
		? bridgeApi.user_state?.chats?.[bridgeApi.user_state.current_chat_id]
		: null;

	return (
		<div className="absolute inset-0 top-16 flex gap-4 px-4 pb-4">
			{/* Left column: Quick Actions and Snapshots */}
			<div className="flex w-80 flex-col gap-4 overflow-hidden">
				{/* Quick Actions - top half */}
				<div className="flex h-1/2 flex-col overflow-hidden">
					<TestingUtils />
				</div>

				{/* Snapshots - bottom half */}
				<div className="flex h-1/2 flex-col overflow-hidden">
					<SnapshotManager
						currentUserState={bridgeApi.user_state || ({} as UserState)}
						onLoadSnapshot={handleLoadSnapshot}
					/>
				</div>
			</div>

			{/* Middle pane: Current Chat Messages */}
			<div className="flex w-96 flex-col overflow-hidden">
				<div
					className="flex items-center justify-between rounded-t-lg border-b
						border-gray-200 bg-gray-50 p-4"
				>
					<h2 className="text-lg font-semibold">Current Chat Messages</h2>
				</div>
				<div className="min-h-0 flex-1">
					<JsonEditor data={chatData} readOnly />
				</div>
			</div>

			{/* Right pane: User State */}
			<div className="flex flex-1 flex-col overflow-hidden">
				<div
					className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200
						bg-gray-50 p-4"
				>
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold">User State</h2>
						<button
							type="button"
							title="Clears user state in Redis and wipes all Data Layer records (agents, notepads, artifacts, metrics, etc.) for this user."
							onClick={handleResetState}
							disabled={resetLoading}
							className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white
								transition hover:bg-blue-700 disabled:cursor-not-allowed
								disabled:opacity-60"
						>
							{resetLoading ? 'Resetting…' : 'Reset State'}
						</button>
					</div>
					{resetError && (
						<p className="text-xs text-red-600">Reset error: {resetError}</p>
					)}
				</div>
				<div className="min-h-0 flex-1">
					<JsonEditor data={bridgeApi.user_state} onSave={handleSaveState} />
				</div>
			</div>

			{/* Far right column: Action Picker */}
			<div className="flex w-[28rem] flex-col overflow-hidden">
				<div
					className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200
						bg-gray-50 p-4"
				>
					<h2 className="text-lg font-semibold">Action Picker</h2>
					<input
						type="text"
						value={selectedActionType}
						onChange={(e) => setSelectedActionType(e.target.value)}
						placeholder="action_type_name"
						className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm
							focus:border-blue-500 focus:ring-2 focus:ring-blue-200
							focus:outline-none"
					/>
					<p className="text-xs text-gray-500">Enter any bridge action type.</p>
				</div>
				<div className="min-h-0 flex-1">
					<JsonEditor
						data={actionEditorData}
						onSave={handleSendAction}
						saveLabel="Send"
					/>
				</div>
			</div>
		</div>
	);
};
